import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api, errMsg } from "../lib/api";
import { PasscodeGate } from "../components/setup/PasscodeGate";
import { SetupHeader } from "../components/setup/SetupHeader";
import { Bench } from "../components/setup/Bench";
import { SealPanel } from "../components/setup/SealPanel";
import { WipeDialog } from "../components/setup/WipeDialog";
import { ReinterpretPanel } from "../components/setup/ReinterpretPanel";
import { SpecimenTable } from "../components/setup/SpecimenTable";
import { SpecimenForm } from "../components/setup/SpecimenForm";
import { MessageEditor } from "../components/setup/MessageEditor";
import { ReaderTest } from "../components/setup/ReaderTest";
import { PushLivePanel } from "../components/setup/PushLivePanel";
import { ConsolePanel } from "../components/setup/ConsolePanel";

const TAB = "rounded-none font-mono uppercase tracking-widest text-xs";

export default function Setup() {
  const [authed, setAuthed] = useState(null);
  const [specimens, setSpecimens] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(
    () => Promise.all([api.get("/admin/specimens"), api.get("/admin/settings")]).then(([s, st]) => { setSpecimens(s.data); setSettings(st.data); }),
    [],
  );

  useEffect(() => { api.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false)); }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (err) { toast.error(errMsg(err, "Something went wrong")); }
  };
  const remove = (s) => run(() => api.delete(`/admin/specimens/${s.id}`), `${s.name} removed`);
  const wipe = () => run(() => api.post("/admin/wipe"), "All objects wiped. Starting from zero.");
  const seal = () => run(() => api.post("/admin/seal"), "Vocabulary sealed. The experiment has begun.");
  const unseal = () => run(() => api.post("/admin/unseal"), "Vocabulary unsealed. Editing enabled.");
  const logout = async () => { await api.post("/admin/logout"); setAuthed(false); };

  if (authed === null) return <div className="lab-screen" />;
  if (!authed) return <PasscodeGate onUnlock={() => setAuthed(true)} />;
  if (!settings) return <div className="lab-screen" />;

  return (
    <div data-testid="setup-page" className="lab-screen">
      <div className="relative z-10 mx-auto max-w-6xl px-5 py-8 sm:px-10">
        <SetupHeader sealed={settings.sealed} onLogout={logout} />
        {settings.sealed ? (
          <div className="mt-8 grid gap-6">
            <SealPanel count={specimens.length} onUnseal={unseal} />
            <PushLivePanel count={specimens.length} />
            <Tabs defaultValue="reader">
              <TabsList className="bg-slate-900 border border-slate-800 rounded-none">
                <TabsTrigger data-testid="tab-reader" value="reader" className={TAB}>Reader check</TabsTrigger>
                <TabsTrigger data-testid="tab-console" value="console" className={TAB}>Console check</TabsTrigger>
                <TabsTrigger data-testid="tab-settings" value="settings" className={TAB}>Lock, voices &amp; story</TabsTrigger>
              </TabsList>
              <TabsContent value="reader" className="mt-6"><ReaderTest specimens={specimens} onLinked={load} readOnly /></TabsContent>
              <TabsContent value="console" className="mt-6"><ConsolePanel /></TabsContent>
              <TabsContent value="settings" className="mt-6"><MessageEditor onSaved={load} /></TabsContent>
            </Tabs>
            <SpecimenTable specimens={specimens} readOnly />
          </div>
        ) : (
          <Tabs defaultValue="bench" className="mt-8">
            <TabsList className="bg-slate-900 border border-slate-800 rounded-none">
              <TabsTrigger data-testid="tab-bench" value="bench" className={TAB}>Bench</TabsTrigger>
              <TabsTrigger data-testid="tab-specimens" value="specimens" className={TAB}>Objects ({specimens.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-console" value="console" className={TAB}>Console check</TabsTrigger>
              <TabsTrigger data-testid="tab-settings" value="settings" className={TAB}>Lock, case file, voices &amp; story</TabsTrigger>
            </TabsList>
            <TabsContent value="bench" className="mt-6">
              <Bench specimens={specimens} target={settings.target_count} onSaved={load} onSeal={seal} />
            </TabsContent>
            <TabsContent value="specimens" className="mt-6 grid gap-4">
              <ReaderTest specimens={specimens} onLinked={load} />
              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex flex-wrap gap-3">
                  <button data-testid="add-specimen-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="lab-btn-primary"><Plus size={16} /> Add manually</button>
                  <ReinterpretPanel count={specimens.length} onDone={load} />
                </div>
                <WipeDialog count={specimens.length} onWipe={wipe} />
              </div>
              <SpecimenTable specimens={specimens} onEdit={(s) => { setEditing(s); setFormOpen(true); }} onDelete={remove} />
            </TabsContent>
            <TabsContent value="console" className="mt-6"><ConsolePanel /></TabsContent>
            <TabsContent value="settings" className="mt-6 grid gap-6">
              <MessageEditor onSaved={load} />
              <PushLivePanel count={specimens.length} />
            </TabsContent>
          </Tabs>
        )}
      </div>
      <SpecimenForm open={formOpen} specimen={editing} onClose={() => setFormOpen(false)} onSaved={load} />
    </div>
  );
}
